import * as t from 'io-ts';
import { createActions } from 'redux-actions';
import { v1 } from 'uuid';

import { ENDPOINTS_OPERATORS, ENDPOINTS_SIMULATIONS } from '../../config';
import { localeData } from '../../locales/data';
import { SimulationsValidation } from '../../reducers/EditorReducer/EditorReducer.types';
import { LanguageFieldsActionPayload } from '../../reducers/LanguageFieldsReducer/LanguageFieldsReducer.types';
import { cachedFetch, globalFetch, ioTsValidationError, isAction, webSimApiErrorFlashMessages } from '../../shared/helpers/fetch-helper';
import { logErrorService } from '../../shared/services';

import { addFlashMessages } from '../FlashMessagesActions';
import { CloneAlphaData, Example } from '../EditorActions.types';
import { getAllExamples } from '../../reducers/EditorReducer/EditorSelector';
import Immutable, { merge } from 'immutable';
import { ImmutableInterface } from '../../types';
import { getLocalStorageItem, setLocalStorageItem } from '../../shared/helpers/local-storage';
import { getUserIdSelector } from '../../reducers/AuthenticationReducer/AuthenticationSelector';

export const {
  updateSettingsActiveTab,
  cloneAlpha,
  cloneAlphaFromContent,
  cloneAlphaFromSimulate,
  changeToMultiTab,
  responseOptions,
  responseExpressionLanguage,
  responseFastExpressionLanguage,
  responseLanguageTokens,
  setTabActive,
  setChildTabActive,
  closeTab,
  closeChildTab,
  changeToSimulation,
  setAllExamples,
  setTabActiveSettingsValue,
  setTabCode,
  setTabLearnPathname,
  setTabDataExplorerPathname,
  setTabTutorialPathname,
  setTabExample,
  setTabCodeError,
  createNewChildTab,
  createNewTab,
  createNewSuperAlphaTab,
  createNewMultiTab,
  renameTab,
  requestOptions,
  responseOptionsFailure,
  requestExpressionLanguage,
  responseExpressionLanguageFailure,
  requestFastExpressionLanguage,
  responseFastExpressionLanguageFailure,
  requestLanguageFields,
  responseLanguageTokensFailure,
  requestExample,
  responseExampleFailure,
  optionsLogout,
  toggleVisualization,
  changeCodeEditorWidth,
  changeTabPosition,
  changeTabPositionChild,
  changingTabPosition,
  stopChangingTabPosition,
} = createActions(
  {
    UPDATE_SETTINGS_ACTIVE_TAB: (settings: any, tabIndex: number, childActiveIndex: number) => ({ settings, tabIndex, childActiveIndex }),
    CLONE_ALPHA: (tabIndex: number, indexChild: number, _: any, childIds: any[], isMulti?: boolean, codeType?: string) => ({
      tabIndex,
      indexChild,
      idParent: v1(),
      idChilds: childIds,
      isMulti,
      codeType,
    }),
    CLONE_ALPHA_FROM_CONTENT: (data: CloneAlphaData) => ({ data, idParent: v1() }),
    CLONE_ALPHA_FROM_SIMULATE: (data: any, tabIndex: number, childActiveIndex: number) => ({ data, tabIndex, childActiveIndex }),
    CHANGE_TO_MULTI_TAB: (tabIndex: number) => ({ tabIndex, idParent: v1(), idChilds: [v1(), v1()] }),
    RESPONSE_OPTIONS: user => user,
    RESPONSE_EXPRESSION_LANGUAGE: expression => expression,
    RESPONSE_FAST_EXPRESSION_LANGUAGE: fastExpression => fastExpression,
    RESPONSE_LANGUAGE_TOKENS: languageFields => languageFields,
    SET_TAB_ACTIVE: (tabIndex: number) => ({ tabIndex }),
    SET_CHILD_TAB_ACTIVE: (tabIndex: number, parentTabIndex: number) => ({ tabIndex, parentTabIndex }),
    CLOSE_TAB: (tabIndex: number) => ({ tabIndex }),
    CLOSE_CHILD_TAB: (tabIndex: number, indexChild: number) => ({ tabIndex, indexChild }),
    CHANGE_TO_SIMULATION: (tabIndex: number, indexChild: number) => ({ tabIndex, indexChild }),
    SET_ALL_EXAMPLES: (examples: Example[]) => ({
      examples,
    }),
    SET_TAB_ACTIVE_SETTINGS_VALUE: (name: string, tabIndex: number, value: any, childActiveIndex: number, updateAll: boolean) => ({
      name,
      tabIndex,
      value,
      childActiveIndex,
      updateAll,
    }),
    SET_TAB_CODE: (code: any, codeType: string, tabIndex: number, childActiveIndex: number) => ({
      tabIndex,
      code,
      codeType,
      childActiveIndex,
    }),
    SET_TAB_LEARN_PATHNAME: (value: any) => ({
      value,
    }),
    SET_TAB_DATA_EXPLORER_PATHNAME: (value: any) => ({
      value,
    }),
    SET_TAB_TUTORIAL_PATHNAME: (value: any) => ({
      value,
    }),
    SET_TAB_EXAMPLE: (example: any, tabIndex: number, childActiveIndex: number) => ({
      tabIndex,
      example,
      childActiveIndex,
    }),
    SET_TAB_CODE_ERROR: (codeError: any, tabIndex: number, childActiveIndex: number) => ({
      tabIndex,
      codeError,
      childActiveIndex,
    }),
    CREATE_NEW_CHILD_TAB: (tabIndex: number, data: any) => ({ tabIndex, data, idChilds: [v1()] }),
    CREATE_NEW_TAB: (data: any) => ({ data, idParent: v1() }),
    CREATE_NEW_SUPER_ALPHA_TAB: (data: any) => ({ data, idParent: v1() }),
    CREATE_NEW_MULTI_TAB: (data: any) => ({ data, idParent: v1(), idChilds: [v1(), v1()] }),
    RENAME_TAB: (tabIndex: number, indexChild: number, newName: string) => ({ tabIndex, indexChild, newName }),
  },
  'REQUEST_OPTIONS',
  'RESPONSE_OPTIONS_FAILURE',
  'REQUEST_EXPRESSION_LANGUAGE',
  'REQUEST_FAST_EXPRESSION_LANGUAGE',
  'REQUEST_LANGUAGE_FIELDS',
  'RESPONSE_EXPRESSION_LANGUAGE_FAILURE',
  'RESPONSE_FAST_EXPRESSION_LANGUAGE_FAILURE',
  'RESPONSE_LANGUAGE_TOKENS_FAILURE',
  'OPTIONS_LOGOUT',
  'REQUEST_EXAMPLE',
  'RESPONSE_EXAMPLE_FAILURE',
  'TOGGLE_VISUALIZATION',
  'CHANGE_CODE_EDITOR_WIDTH',
  'CHANGE_TAB_POSITION',
  'CHANGE_TAB_POSITION_CHILD',
  'CHANGING_TAB_POSITION',
  'STOP_CHANGING_TAB_POSITION'
);

export function setActiveTabLanguage(settings: any, tabIndex: number, childActiveIndex: number) {
  return async (dispatch: any): Promise<object> => {
    dispatch(updateSettingsActiveTab(settings, tabIndex, childActiveIndex));
    return dispatch(
      getLanguageFields({
        instrument: settings.instrumentType.value,
        universe: settings.universe.value,
        delay: settings.delay.value,
        region: settings.region.value,
      })
    );
  };
}

export function setActiveChildTab(tabIndex: number, parentTabIndex: number) {
  return async (dispatch: any, getState: any): Promise<object> => {
    dispatch(setChildTabActive(tabIndex, parentTabIndex));

    const state = getState();
    const settings = getAlphaSettings(state, parentTabIndex).toJS();
    return dispatch(
      getLanguageFields({
        instrument: settings.instrumentType.value,
        universe: settings.universe.value,
        delay: settings.delay.value,
        region: settings.region.value,
      })
    );
  };
}

export const getAlphaSettings = (state: any, tabIndex: number) => {
  if (tabIndex < 0) {
    return null;
  }
  const tab = state.getIn(['editor', 'tabs', tabIndex]);
  const activeChildTab = tab.get('activeChildTab');
  const tabSettings = tab.getIn(['settings']);
  if (typeof activeChildTab !== 'number') {
    return tabSettings;
  }

  const childTabSettings = tab.getIn(['childTabs', activeChildTab, 'settings']);
  return merge(tabSettings, childTabSettings);
};

export function getFastExpressionLanguage() {
  return async (dispatch: any): Promise<object> => {
    dispatch(requestFastExpressionLanguage());

    return globalFetch(
      ENDPOINTS_OPERATORS.operators,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseFastExpressionLanguageFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_FAST_EXPRESSION_LANGUAGE_FAILURE')) {
          return json;
        } else {
          return dispatch(responseFastExpressionLanguage(json));
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        webSimApiErrorFlashMessages(dispatch);

        return dispatch(responseFastExpressionLanguageFailure());
      });
  };
}

export interface LanguageFieldArguments {
  instrument?: string;
  region?: string;
  delay?: number | string;
  universe?: string;
}

interface FieldResponse {
  count: number;
  results: LanguageField[];
}

export interface LanguageField {
  id: string;
  description?: string;
  type?: string;
  category: {
    id: string;
    name: string;
  };
  subcategory: {
    id: string;
    name: string;
  };
  dataset: {
    id: string;
    name: string;
  };
}

export function getLanguageFields({ instrument, region, delay, universe }: LanguageFieldArguments) {
  return async (dispatch: any): Promise<object> => {
    dispatch(requestLanguageFields());
    const query =
      instrument && region && universe && (delay || delay === 0)
        ? `?instrumentType=${instrument}&region=${region}&delay=${delay}&universe=${universe}`
        : '';

    if (query === '' && (instrument || region || delay || delay === 0 || universe)) {
      logErrorService(new Error('Request specified partial parameters for data-fields request. They were not used.'));
    }

    const url = query !== '' ? `${ENDPOINTS_SIMULATIONS.new_language_fields}${query}` : ENDPOINTS_SIMULATIONS.all_language_fields;
    const fieldsPromise = cachedFetch(
      url,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseLanguageTokensFailure()),
      { endpointVersion: 'version=3.0' }
    ).then(async (json: FieldResponse): Promise<object> => {
      if ('results' in json) {
        return json.results;
      }
      return json;
    });

    const globalAutocompletePromise = cachedFetch(
      `${ENDPOINTS_SIMULATIONS.language_fields}`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseLanguageTokensFailure()),
      { endpointVersion: 'version=4.0' }
    ).then(async (json: LanguageFieldsActionPayload): Promise<object> => {
      return json;
    });
    try {
      const [fieldsResponse, autocompleteResponse] = await Promise.all([fieldsPromise, globalAutocompletePromise])
        .then(values => values as [LanguageField[], LanguageFieldsActionPayload])
        .catch((err: any) => {
          logErrorService(err, err);
          webSimApiErrorFlashMessages(dispatch);

          return dispatch(responseLanguageTokensFailure());
        });

      if (isAction(fieldsResponse, 'RESPONSE_LANGUAGE_TOKENS_FAILURE')) {
        return fieldsResponse;
      }

      if (isAction(autocompleteResponse, 'RESPONSE_LANGUAGE_TOKENS_FAILURE')) {
        return autocompleteResponse;
      }

      const newResponse = {
        regular:
          (fieldsResponse as LanguageField[])?.map(({ id, description, type, dataset, category, subcategory }) => ({
            id,
            description,
            type,
            dataset,
            category,
            subcategory,
          })) ?? [],
        combo: autocompleteResponse?.combo?.map((id: string) => ({ id })) ?? [],
        selection: autocompleteResponse?.selection?.map((id: string) => ({ id })) ?? [],
      };

      return dispatch(responseLanguageTokens(newResponse));
    } catch (e) {
      webSimApiErrorFlashMessages(dispatch);

      return dispatch(responseLanguageTokensFailure());
    }
  };
}

const EXAMPLE_REQUEST_LIMIT = `50`;

export function getNextExampleNumber(userId: string, count: number) {
  const parsedLastExampleNumber = parseInt(getLocalStorageItem(userId, 'alphaExampleNumber'));
  if (isNaN(parsedLastExampleNumber)) {
    logErrorService(
      new Error(
        `wrong getNextExampleNumber parsedLastExampleNumber: ${parsedLastExampleNumber}, count: ${count} localStorage alphaExampleNumber: ${getLocalStorageItem(
          userId,
          'alphaExampleNumber'
        )}`
      )
    );
  }
  const lastExampleNumber = isNaN(parsedLastExampleNumber) ? -1 : Math.max(parsedLastExampleNumber, -1);
  const nextExampleNumber = lastExampleNumber >= count - 1 ? 0 : lastExampleNumber + 1;
  setLocalStorageItem(userId, 'alphaExampleNumber', nextExampleNumber);
  return nextExampleNumber;
}

export function getExample(currentTabIndex: number, childActiveTab: number) {
  return async (dispatch: any, getState: any): Promise<object> => {
    dispatch(requestExample());

    const state = getState();

    const userId = getUserIdSelector(state) as string;

    const examples = getAllExamples(state) as Immutable.List<ImmutableInterface<Example>>;
    if (examples?.size) {
      const nextExampleNumber = getNextExampleNumber(userId, examples?.size);

      return dispatch(setTabExample(examples.get(nextExampleNumber)?.toJS(), currentTabIndex, childActiveTab));
    }

    return globalFetch(
      `${ENDPOINTS_SIMULATIONS.example}?limit=${EXAMPLE_REQUEST_LIMIT}`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseExampleFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_EXAMPLE_FAILURE')) {
          return json;
        } else if (json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);

          return dispatch(responseExampleFailure());
        } else {
          dispatch(setAllExamples(json.results));
          const nextExampleNumber = getNextExampleNumber(userId, json.results.length);
          return dispatch(setTabExample(json.results[nextExampleNumber], currentTabIndex, childActiveTab));
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        webSimApiErrorFlashMessages(dispatch);

        return dispatch(responseExampleFailure());
      });
  };
}

export function fetchSimulationOptions() {
  return async (dispatch: any) => {
    dispatch(requestOptions());

    return globalFetch(
      ENDPOINTS_SIMULATIONS.simulations,
      {
        method: 'OPTIONS',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseOptionsFailure()),
      {
        endpointVersion: 'version=3.0',
      }
    )
      .then((json: any) => {
        if (isAction(json, 'RESPONSE_OPTIONS_FAILURE')) {
          return json;
        } else if (t.exact(SimulationsValidation).decode(json).isRight()) {
          return dispatch(responseOptions(json.actions.POST.settings.children));
        } else if (json && json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);

          return dispatch(responseOptionsFailure());
        } else {
          // It will be reported to sentry the io-ts validation errors
          ioTsValidationError(json, SimulationsValidation);
          dispatch(
            addFlashMessages([{ msg: 'editorSettings.error.message', defaultMessage: localeData.en['editorSettings.error.message'] }])
          );

          return dispatch(responseOptionsFailure());
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        webSimApiErrorFlashMessages(dispatch);

        return dispatch(responseOptionsFailure());
      });
  };
}
